import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ACCOUNT_ACTIONS_REQUIRING_REASON,
  AccountAction,
  accountActionsFor,
  isAdminRole,
  type AccountStatus,
  type AccountStatusActionInput,
} from '@lombok/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusPill } from '@/components/StatusPill';
import { TextareaField } from '@/components/form';
import { useAuth } from '@/auth/AuthContext';
import { useDocuments } from '@/data/hooks';
import { ACCOUNT_ACTION_COPY, RIF_STATE_LABEL, rifState, statusReasonLabel } from './accountActions';

interface AccountStatusPanelProps {
  accountId: string;
  status: AccountStatus;
  statusReason?: string;
  /** Runs the action on the server; a refusal (409) is shown inside the dialog. */
  onChange: (input: AccountStatusActionInput) => Promise<unknown>;
}

/**
 * Estado de la cuenta (customer and distributor detail, admin). Shows the status, why it was
 * last changed and whether the RIF is approved, and offers only the actions the shared
 * transition table allows from the current status. Approving needs an approved RIF (the
 * server enforces it too); rejecting, holding and suspending ask for a reason.
 */
export function AccountStatusPanel({ accountId, status, statusReason, onChange }: AccountStatusPanelProps) {
  const { session } = useAuth();
  // Only admins change the status (the API answers 403 to everyone else): others just read it.
  const canManage = session ? isAdminRole(session.user.role) : false;
  const documents = useDocuments(canManage ? accountId : undefined);
  const [action, setAction] = useState<AccountAction | null>(null);
  const [reason, setReason] = useState('');

  const items = documents.data?.items ?? [];
  const rif = rifState(items);
  // Only block the button when we saw every document and none is an approved RIF; if the list
  // is still loading or has more pages, let the server decide.
  const rifKnownMissing = documents.isSuccess && !documents.data?.nextCursor && rif !== 'approved';

  const copy = action ? ACCOUNT_ACTION_COPY[action] : undefined;
  const needsReason = action ? ACCOUNT_ACTIONS_REQUIRING_REASON.includes(action) : false;
  const reasonLabel = statusReasonLabel(status);

  const open = (next: AccountAction) => {
    setReason('');
    setAction(next);
  };

  if (!canManage) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Estado de la cuenta</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3 pt-0">
          <StatusPill kind="account" value={status} />
          {reasonLabel && statusReason ? (
            <p className="text-sm text-text-muted">
              <span className="font-medium text-text-primary">{reasonLabel}:</span> {statusReason}
            </p>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Estado de la cuenta</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        <div className="flex flex-wrap items-center gap-3">
          <StatusPill kind="account" value={status} />
          {reasonLabel && statusReason ? (
            <p className="text-sm text-text-muted">
              <span className="font-medium text-text-primary">{reasonLabel}:</span> {statusReason}
            </p>
          ) : null}
        </div>

        <p className="text-sm text-text-muted">
          {documents.isLoading ? 'Revisando documentos…' : RIF_STATE_LABEL[rif]}
          {' · '}
          <Link to="/app/admin/documentos" className="font-medium text-brand-primary hover:underline">
            Revisar documentos
          </Link>
        </p>

        <div className="flex flex-wrap gap-2">
          {accountActionsFor(status).map((a) => {
            const blocked = a === AccountAction.APPROVE && rifKnownMissing;
            return (
              <Button
                key={a}
                size="sm"
                variant={ACCOUNT_ACTION_COPY[a].tone}
                onClick={() => open(a)}
                disabled={blocked}
                title={blocked ? 'Falta el RIF aprobado' : undefined}
              >
                {ACCOUNT_ACTION_COPY[a].button}
              </Button>
            );
          })}
        </div>
        {status === 'pending' && rifKnownMissing ? (
          <p className="text-sm text-text-muted">
            Para aprobar la cuenta primero hay que aprobar el documento RIF (Documentos → Pendientes).
          </p>
        ) : null}
      </CardContent>

      <ConfirmDialog
        open={action !== null}
        title={copy?.title ?? ''}
        confirmLabel={copy?.confirm ?? ''}
        danger={copy?.tone === 'danger'}
        confirmDisabled={needsReason && reason.trim().length === 0}
        onConfirm={() => onChange({ action: action as AccountAction, reason: reason.trim() || undefined })}
        onClose={() => setAction(null)}
      >
        <div className="flex flex-col gap-3">
          <p>{copy?.effect}</p>
          {action === AccountAction.APPROVE ? null : (
            <TextareaField
              label={needsReason ? 'Motivo' : 'Motivo (opcional)'}
              required={needsReason}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              name="status-reason"
            />
          )}
        </div>
      </ConfirmDialog>
    </Card>
  );
}
